/**
 * Mini Cart Garden Marker Upsell Handler
 * Dynamically searches and matches Garden Marker variants based on Rose SKU prefixes ([Prefix] TAG-BLACK / [Prefix] TAG-PINK).
 * Handles variant selection, AJAX cart addition, and quantity synchronization matching Rose items.
 */

class MiniCartGardenMarkerUpsell extends HTMLElement {
  constructor() {
    super();
    /** @type {string | null} */
    this.selectedColor = null;
    /** @type {string | null} */
    this.selectedVariantId = null;
    /** @type {string | null} */
    this.skuPrefix = null;
    /** @type {number} */
    this.roseQuantity = 1;
    /** @type {Record<string, any>} */
    this.variants = {
      Black: null,
      Pink: null
    };
  }

  connectedCallback() {
    this.initElements();
    this.bindEvents();
  }

  initElements() {
    /** @type {HTMLElement | null} */
    this.banner = this.querySelector('.upsell-addon-banner');
    /** @type {HTMLElement | null} */
    this.bannerBtnYes = this.querySelector('.upsell-banner-btn-yes');
    /** @type {HTMLElement | null} */
    this.configPanel = this.querySelector('.upsell-config-panel');
    /** @type {HTMLElement | null} */
    this.optionsWrapper = this.querySelector('.upsell-options-wrapper');
    /** @type {NodeListOf<HTMLElement>} */
    this.colorCards = this.querySelectorAll('.upsell-color-card');
    /** @type {HTMLElement | null} */
    this.addCta = this.querySelector('.upsell-add-cta');
    /** @type {HTMLElement | null} */
    this.priceRow = this.querySelector('.upsell-price-row');
    /** @type {HTMLElement | null} */
    this.successBanner = this.querySelector('.upsell-success-banner');
    /** @type {HTMLElement | null} */
    this.successDetail = this.querySelector('.upsell-success-detail');
    /** @type {HTMLElement | null} */
    this.confirmOverlay = this.querySelector('.upsell-qty-confirm-box');
    /** @type {HTMLElement | null} */
    this.confirmQtyNum = this.querySelector('.confirm-qty-num');
    /** @type {HTMLElement | null} */
    this.confirmQtyBtnNum = this.querySelector('.confirm-qty-btn-num');
    /** @type {HTMLElement | null} */
    this.btnConfirmAll = this.querySelector('.upsell-btn-confirm-all');
    /** @type {HTMLElement | null} */
    this.btnConfirmSingle = this.querySelector('.upsell-btn-confirm-single');
    /** @type {HTMLElement | null} */
    this.btnConfirmCancel = this.querySelector('.upsell-btn-confirm-cancel');
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '$7.00';
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Evaluates if a given product item is a Rose product
   * @param {string} [type]
   * @returns {boolean}
   */
  isRoseProduct(type = '') {
    return (type || '').toLowerCase().includes('rose');
  }

  bindEvents() {
    if (this.bannerBtnYes) {
      this.bannerBtnYes.addEventListener('click', () => this.toggleConfigPanel());
    }

    if (this.colorCards && this.colorCards.length > 0) {
      this.colorCards.forEach(card => {
        card.addEventListener('click', (e) => {
          const target = /** @type {HTMLElement} */ (e.currentTarget);
          this.handleColorSelect(target);
        });
      });
    }

    if (this.addCta) {
      this.addCta.addEventListener('click', () => this.handleAddCtaClick());
    }

    if (this.btnConfirmAll) {
      this.btnConfirmAll.addEventListener('click', (e) => {
        const target = /** @type {HTMLElement} */ (e.currentTarget);
        this.executeAddToCart(this.roseQuantity, target);
      });
    }

    if (this.btnConfirmSingle) {
      this.btnConfirmSingle.addEventListener('click', (e) => {
        const target = /** @type {HTMLElement} */ (e.currentTarget);
        this.executeAddToCart(1, target);
      });
    }

    if (this.btnConfirmCancel) {
      this.btnConfirmCancel.addEventListener('click', () => {
        this.hideConfirmOverlay();
        if (this.addCta) {
          this.addCta.removeAttribute('data-added');
          this.addCta.style.pointerEvents = 'auto';
        }
      });
    }

    // Listen for AJAX product addition to evaluate newly added Rose items in real-time
    document.addEventListener('ajaxProduct:added', async (evt) => {
      const customEvt = /** @type {CustomEvent} */ (evt);
      const rawProd = (customEvt && customEvt.detail && customEvt.detail.product) ? customEvt.detail.product : null;
      if (!rawProd) return;

      // Extract item from single vs batch (/cart/add.js) responses
      let item = (rawProd.items && Array.isArray(rawProd.items) && rawProd.items.length > 0)
        ? rawProd.items[rawProd.items.length - 1]
        : rawProd;

      let pType = (item.product_type || '').toLowerCase();
      let sku = (item.sku || '').trim();
      let isRose = this.isRoseProduct(pType);
      const prefix = sku ? sku.split(' ')[0] : '';

      // Use exact submitted quantity from event detail (prevents Shopify API cumulative line-item total e.g. 9)
      let submittedQty = (customEvt && customEvt.detail && customEvt.detail.quantity !== undefined) ? customEvt.detail.quantity : null;
      if (typeof submittedQty === 'string') {
        submittedQty = parseInt(submittedQty, 10);
      }
      if (!submittedQty || isNaN(submittedQty)) {
        const activeQtyInput = /** @type {HTMLInputElement | null} */ (
          document.querySelector('product-form-component input[name="quantity"]') || document.querySelector('.quantity__input')
        );
        if (activeQtyInput && activeQtyInput.value) {
          submittedQty = parseInt(activeQtyInput.value, 10);
        }
      }

      const addedQty = (typeof submittedQty === 'number' && !isNaN(submittedQty) && submittedQty > 0)
        ? submittedQty
        : 1;

      if (isRose && prefix) {
        try {
          sessionStorage.setItem('upsell_last_added_qty', String(addedQty));
          sessionStorage.setItem('upsell_last_added_prefix', prefix);
        } catch (e) {}

        this.dataset.hasRose = 'true';
        this.dataset.rosePrefix = prefix;
        this.dataset.roseQuantity = String(addedQty);
        this.roseQuantity = addedQty;

        this.resetState();
        this.checkAndFetchMatches();
      } else {
        this.dataset.hasRose = 'false';
        this.dataset.rosePrefix = '';
        this.style.display = 'none';
      
      }
    });

    document.addEventListener('cart:close', () => {
      this.clearSessionQty();
      this.resetState();
    });
    document.addEventListener('headerDrawer:closed', () => {
      this.clearSessionQty();
      this.resetState();
    });
    document.addEventListener('dialog:close', () => {
      this.clearSessionQty();
      this.resetState();
    });
  }

  clearSessionQty() {
    try {
      sessionStorage.removeItem('upsell_last_added_qty');
      sessionStorage.removeItem('upsell_last_added_prefix');
    } catch (e) {}
  }


  resetState() {
    if (this.banner) this.banner.style.display = 'block';
    if (this.bannerBtnYes) {
      this.bannerBtnYes.textContent = 'YES';
      this.bannerBtnYes.classList.remove('open');
    }
    if (this.configPanel) this.configPanel.classList.remove('open');
    if (this.successBanner) this.successBanner.classList.remove('show');
    this.hideConfirmOverlay();
    if (this.addCta) {
      this.addCta.removeAttribute('data-added');
      this.addCta.classList.remove('ready');
      this.addCta.style.pointerEvents = '';
    }
    if (this.priceRow) {
      this.priceRow.textContent = 'Garden marker';
    }
    if (this.colorCards && this.colorCards.length > 0) {
      this.colorCards.forEach(card => card.classList.remove('selected'));
    }
    this.selectedColor = null;
    this.selectedVariantId = null;
    this.variants = { Black: null, Pink: null };
  }

  async checkAndFetchMatches() {
    const hasRose = this.dataset.hasRose === 'true';
    this.skuPrefix = (this.dataset.rosePrefix || '').trim();

    let storedQty = null;
    try {
      const savedPrefix = sessionStorage.getItem('upsell_last_added_prefix');
      const savedQty = sessionStorage.getItem('upsell_last_added_qty');
      if (savedPrefix === this.skuPrefix && savedQty) {
        storedQty = parseInt(savedQty, 10);
      }
    } catch (e) {}

    this.roseQuantity = storedQty || parseInt(this.dataset.roseQuantity || '1', 10);

    if (!hasRose || !this.skuPrefix) {
      this.style.display = 'none';
      return;
    }

    try {
      const matchFound = await this.searchGardenMarkerProducts(this.skuPrefix);
      if (matchFound) {
        this.style.display = 'block';
      } else {
        this.style.display = 'none';
      }
    } catch (err) {
      console.warn('Garden Marker upsell lookup error:', err);
      this.style.display = 'none';
    }
  }

  /**
   * @param {string} prefix
   * @returns {Promise<boolean>}
   */
  async searchGardenMarkerProducts(prefix) {
    /** @type {Set<string>} */
    const candidateHandles = new Set();
    const cleanPrefix = prefix.toUpperCase().trim();

    // 1. Query Storefront Predictive Search API with SKU fields enabled
    try {
      const suggestUrl = `/search/suggest.json?q=${encodeURIComponent(cleanPrefix)}&resources[type]=product&resources[limit]=10&resources[options][prefix]=last&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku`;
      const suggestResp = await fetch(suggestUrl);
      if (suggestResp.ok) {
        const suggestData = await suggestResp.json();
       
        const prods = (suggestData && suggestData.resources && suggestData.resources.results && suggestData.resources.results.products)
          ? suggestData.resources.results.products
          : [];
        prods.forEach((/** @type {any} */ p) => {
          if (p && typeof p.handle === 'string') {
            const pType = (p.type || '').toLowerCase();
            const pTitle = (p.title || '').toLowerCase();
            if (pType.includes('garden marker')) {
              candidateHandles.add(p.handle);
            }
          }
        });
        
      }
    } catch (e) {
      console.warn('Predictive search with SKU fields failed:', e);
    }

    const targetBlackSku = `${cleanPrefix} TAG-BLACK`;
    const targetPinkSku = `${cleanPrefix} TAG-PINK`;
    let foundAny = false;

    // Inspect candidate products' variant SKUs
    for (const handle of candidateHandles) {
      try {
        const prodResp = await fetch(`/products/${handle}.js`);
      
        if (!prodResp.ok) continue;

        const productData = await prodResp.json();
        if (!productData || !productData.variants || !Array.isArray(productData.variants)) continue;
        const isGardenMarkerType = (productData.type || '').toLowerCase().includes('garden marker');
        if (!isGardenMarkerType && candidateHandles.size > 1) {
          continue;
        }

        productData.variants.forEach((/** @type {any} */ variant) => {
          const varSku = (variant.sku || '').toUpperCase().trim();
          const varTitle = (variant.title || '').toLowerCase();

          // Match Black variant
          if (
            varSku === targetBlackSku ||
            (varSku.includes(cleanPrefix) && (varSku.includes('BLACK')))
          ) {
            this.variants.Black = variant;
            foundAny = true;
          }

          // Match Pink variant
          if (
            varSku === targetPinkSku ||
            (varSku.includes(cleanPrefix) && (varSku.includes('PINK')))
          ) {
            this.variants.Pink = variant;
            foundAny = true;
          }
        });

        // Update variant IDs & images on swatch cards
        if (this.colorCards && this.colorCards.length > 0) {
          this.colorCards.forEach(card => {
            const color = card.dataset.color || 'Black';
            const matchedVariant = this.variants[color];

            if (matchedVariant) {
              card.dataset.variantId = String(matchedVariant.id);
              card.dataset.price = String(matchedVariant.price);
              card.classList.remove('disabled');

              const img = /** @type {HTMLImageElement | null} */ (card.querySelector('img'));
              if (img) {
                let imgSrc = (matchedVariant.featured_image && matchedVariant.featured_image.src)
                  || (matchedVariant.featured_media && matchedVariant.featured_media.preview_image && matchedVariant.featured_media.preview_image.src)
                  || matchedVariant.image
                  || productData.featured_image
                  || (productData.images && productData.images[0]);
                if (typeof imgSrc === 'object' && imgSrc !== null && imgSrc.src) {
                  imgSrc = imgSrc.src;
                }
                if (typeof imgSrc === 'string' && imgSrc) {
                  if (imgSrc.startsWith('//')) {
                    imgSrc = 'https:' + imgSrc;
                  }
                  img.src = imgSrc;
                }
              }
            } else {
              card.dataset.variantId = '';
              card.dataset.price = '';
              card.classList.add('disabled');
            }
          });
        }

        if (foundAny) break;
      } catch (err) {
        // Continue checking other candidates
      }
    }

    return foundAny;
  }

  /**
   * @param {boolean | null} [forceState]
   */
  toggleConfigPanel(forceState = null) {
    if (!this.configPanel) return;
    const shouldOpen = forceState !== null ? forceState : !this.configPanel.classList.contains('open');

    if (shouldOpen) {
      this.configPanel.classList.add('open');
      if (this.bannerBtnYes) {
        this.bannerBtnYes.textContent = 'NO';
        this.bannerBtnYes.classList.add('open');
      }
    } else {
      this.configPanel.classList.remove('open');
      if (this.bannerBtnYes) {
        this.bannerBtnYes.textContent = 'YES';
        this.bannerBtnYes.classList.remove('open');
      }
    }
  }

  /**
   * @param {HTMLElement} cardElement
   */
  handleColorSelect(cardElement) {
    if (!cardElement || cardElement.classList.contains('disabled')) return;

    if (this.colorCards) {
      this.colorCards.forEach(c => c.classList.remove('selected'));
    }
    cardElement.classList.add('selected');

    this.selectedColor = cardElement.dataset.color || 'Black';
    this.selectedVariantId = cardElement.dataset.variantId || cardElement.getAttribute('data-variant-id') || null;

    const matchedVariant = this.variants[this.selectedColor];
    const priceCents = (matchedVariant && typeof matchedVariant.price === 'number')
      ? matchedVariant.price
      : parseInt(cardElement.dataset.price || '700', 10);
    const qty = Math.max(1, typeof this.roseQuantity === 'number' && !isNaN(this.roseQuantity) ? this.roseQuantity : 1);
    const totalPriceCents = priceCents * qty;
    const totalPriceFormatted = this.formatMoney(totalPriceCents);

    if (this.addCta) {
      this.addCta.classList.add('ready');
      this.addCta.removeAttribute('data-added');
    }

    if (this.priceRow) {
      this.priceRow.textContent = `Garden marker: ${totalPriceFormatted}`;
    }
  }

  showConfirmOverlay() {
    if (!this.confirmOverlay) return;
    if (this.confirmQtyNum) this.confirmQtyNum.textContent = String(this.roseQuantity);
    if (this.confirmQtyBtnNum) this.confirmQtyBtnNum.textContent = String(this.roseQuantity);

    if (this.optionsWrapper) this.optionsWrapper.style.display = 'none';

    this.confirmOverlay.removeAttribute('hidden');
  }

  hideConfirmOverlay() {
    if (!this.confirmOverlay) return;
    this.confirmOverlay.setAttribute('hidden', '');

    if (this.optionsWrapper) this.optionsWrapper.style.display = '';
    if (this.addCta) {
      this.addCta.style.display = '';
      this.addCta.style.pointerEvents = 'auto';
    }
    if (this.priceRow) this.priceRow.style.display = '';
  }

  async handleAddCtaClick() {
    if (!this.selectedColor || !this.selectedVariantId) return;

    const threshold = parseInt(this.dataset.qtyThreshold || '2', 10);
    const validThreshold = isNaN(threshold) ? 2 : threshold;

    if (this.roseQuantity > validThreshold) {
      this.showConfirmOverlay();
      return;
    }

    await this.executeAddToCart(this.roseQuantity);
  }

  /**
   * @param {number | string} qty
   * @param {HTMLElement | null} [targetBtn]
   */
  async executeAddToCart(qty, targetBtn = null) {
    const numericQty = typeof qty === 'number' ? qty : parseInt(qty || '1', 10);
    const finalQuantity = Math.max(1, isNaN(numericQty) ? 1 : numericQty);
    const activeBtn = targetBtn || this.addCta;

    if (activeBtn) {
      activeBtn.dataset.added = 'true';
      activeBtn.style.pointerEvents = 'none';
    }

    try {
      const shopifyGlobal = /** @type {any} */ (window).Shopify;
      const rootUrl = (shopifyGlobal && shopifyGlobal.routes && shopifyGlobal.routes.root) || '/';
      const response = await fetch(`${rootUrl}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: this.selectedVariantId,
              quantity: finalQuantity
            }
          ],
          sections: 'cart-ajax'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add item to cart');
      }

      const resData = await response.json();
    
      // Small delay so user sees checkmark burst animation
      await new Promise(resolve => setTimeout(resolve, 600));

      // Hide banner, config panel, and overlay directly on success
      if (this.banner) this.banner.style.display = 'none';
      if (this.configPanel) this.configPanel.classList.remove('open');
      if (this.confirmOverlay) this.confirmOverlay.setAttribute('hidden', '');

      // Display Success Confirmation Banner
      if (this.successDetail) {
        this.successDetail.textContent = `${this.selectedColor} tag added`;
      }
      if (this.successBanner) {
        this.successBanner.classList.add('show');
      }

      this.clearSessionQty();

      // Fetch fresh cart state to update all cart count bubbles across the theme
      try {
        const cartResp = await fetch('/cart.js', { cache: 'no-store' });
        if (cartResp.ok) {
          const freshCart = await cartResp.json();
          const totalCount = typeof freshCart.item_count === 'number' ? freshCart.item_count : 0;

          // 1. Direct DOM update for top header & mobile cart bubbles
          document.querySelectorAll('.cart-bubble__text-count, .cart-count-bubble, [data-testid="cart-bubble"]').forEach((el) => {
            const htmlEl = /** @type {HTMLElement} */ (el);
            htmlEl.textContent = String(totalCount);
            htmlEl.classList.toggle('hidden', totalCount === 0);
            htmlEl.classList.toggle('visually-hidden', totalCount === 0);
            htmlEl.hidden = totalCount === 0;
          });
          document.querySelectorAll('.cart-bubble').forEach((el) => {
            const htmlEl = /** @type {HTMLElement} */ (el);
            htmlEl.classList.toggle('visually-hidden', totalCount === 0);
          });

          // 2. Update mini cart footer view cart count (e.g. "(3)")
          document.querySelectorAll('[data-view-cart-count]').forEach((el) => {
            el.textContent = `(${String(totalCount)})`;
          });

          // 3. Trigger theme's native <cart-icon> element update
          document.querySelectorAll('cart-icon').forEach((/** @type {any} */ icon) => {
            if (typeof icon.ensureCartBubbleIsCorrect === 'function') {
              icon.ensureCartBubbleIsCorrect();
            }
          });

          // 4. Dispatch standard theme cart update events
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { data: { totalCartCount: totalCount } }
          }));

          document.dispatchEvent(new CustomEvent('cartUpdated', {
            bubbles: true,
            detail: { cart: freshCart }
          }));
        }
      } catch (countErr) {
        console.warn('Failed to refresh cart count:', countErr);
      }


      // Dispatch global cart open event
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));

    } catch (err) {
      console.error('Error adding garden marker to cart:', err);
      if (activeBtn) {
        activeBtn.removeAttribute('data-added');
        activeBtn.style.pointerEvents = 'auto';
      }
    }
  }
}

if (!customElements.get('mini-cart-garden-marker-upsell')) {
  customElements.define('mini-cart-garden-marker-upsell', MiniCartGardenMarkerUpsell);
}

export default MiniCartGardenMarkerUpsell;
